import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  RefreshCw, Link2, Unlink, Play, CheckCircle2, XCircle,
  ArrowLeftRight, Clock, AlertTriangle, Loader2, Trash2
} from 'lucide-react';

export default function CRMSync() {
  const [connections, setConnections] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [form, setForm] = useState({
    provider: '', instance_url: '', api_key: '',
    sync_direction: 'bidirectional', auto_score: true, push_threshold: 70
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [connRes, logRes] = await Promise.all([
        api.get('/api/crm-sync/connections'),
        api.get('/api/crm-sync/sync-logs?limit=10')
      ]);
      setConnections(connRes.data.data || []);
      setSyncLogs(logRes.data.data || []);
    } catch (e) {
      toast.error('Failed to load CRM sync data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.provider) { toast.error('Select a CRM provider'); return; }
    setCreating(true);
    try {
      await api.post('/api/crm-sync/connections', form);
      toast.success(`${form.provider} connection created`);
      setForm({ provider: '', instance_url: '', api_key: '', sync_direction: 'bidirectional', auto_score: true, push_threshold: 70 });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create connection');
    } finally {
      setCreating(false);
    }
  };

  const handleTest = async (id) => {
    try {
      const res = await api.post(`/api/crm-sync/connections/${id}/test`);
      if (res.data.data?.success) {
        toast.success('Connection test passed');
      } else {
        toast.error(res.data.data?.message || 'Connection test failed');
      }
      fetchData();
    } catch (e) {
      toast.error('Connection test failed');
    }
  };

  const handleSync = async (id) => {
    setSyncing(prev => ({ ...prev, [id]: true }));
    try {
      const res = await api.post(`/api/crm-sync/connections/${id}/sync`);
      const job = res.data.data;
      toast.success(`Sync complete: ${job.leads_created} created, ${job.leads_pushed} pushed`);
      fetchData();
    } catch (e) {
      toast.error('Sync failed');
    } finally {
      setSyncing(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this CRM connection?')) return;
    try {
      await api.delete(`/api/crm-sync/connections/${id}`);
      toast.success('Connection deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'connected': return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case 'syncing': return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'error': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Unlink className="h-4 w-4 text-zinc-400" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4" data-testid="crm-sync-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="crm-sync-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">CRM Sync</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Connect your existing CRM to sync leads bi-directionally with AI scoring.
        </p>
      </div>

      {/* Create Connection */}
      <Card data-testid="crm-sync-create-card">
        <CardHeader>
          <CardTitle className="text-base">Add CRM Connection</CardTitle>
          <CardDescription>Connect Salesforce or HubSpot to start syncing leads</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Provider</Label>
              <Select value={form.provider} onValueChange={v => setForm(f => ({ ...f, provider: v }))}>
                <SelectTrigger data-testid="crm-provider-select">
                  <SelectValue placeholder="Select CRM" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="salesforce">Salesforce</SelectItem>
                  <SelectItem value="hubspot">HubSpot</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Instance URL</Label>
              <Input
                data-testid="crm-instance-url"
                placeholder="https://your-org.salesforce.com"
                value={form.instance_url}
                onChange={e => setForm(f => ({ ...f, instance_url: e.target.value }))}
              />
            </div>
            <div>
              <Label>API Key</Label>
              <Input
                data-testid="crm-api-key"
                type="password"
                placeholder="Enter API key"
                value={form.api_key}
                onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <Label>Sync Direction</Label>
              <Select value={form.sync_direction} onValueChange={v => setForm(f => ({ ...f, sync_direction: v }))}>
                <SelectTrigger data-testid="crm-sync-direction">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inbound">Inbound Only</SelectItem>
                  <SelectItem value="outbound">Outbound Only</SelectItem>
                  <SelectItem value="bidirectional">Bidirectional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Push Threshold (AI Score)</Label>
              <Input
                data-testid="crm-push-threshold"
                type="number" min={0} max={100}
                value={form.push_threshold}
                onChange={e => setForm(f => ({ ...f, push_threshold: parseInt(e.target.value) || 70 }))}
              />
            </div>
            <div className="flex items-end">
              <Button
                data-testid="crm-create-btn"
                onClick={handleCreate}
                disabled={creating || !form.provider}
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
                Connect CRM
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Connections */}
      {connections.length > 0 && (
        <div className="space-y-4" data-testid="crm-connections-list">
          <h2 className="text-lg font-semibold">Active Connections</h2>
          {connections.map(conn => (
            <Card key={conn.id} data-testid={`crm-connection-${conn.provider}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    {statusIcon(conn.status)}
                    <div>
                      <div className="font-medium capitalize">{conn.provider}</div>
                      <div className="text-xs text-muted-foreground">{conn.instance_url || 'API connected'}</div>
                    </div>
                    <Badge variant={conn.status === 'connected' ? 'default' : conn.status === 'error' ? 'destructive' : 'secondary'}>
                      {conn.status}
                    </Badge>
                    <Badge variant="outline">
                      <ArrowLeftRight className="h-3 w-3 mr-1" />
                      {conn.sync_direction}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {conn.last_sync_at && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Last sync: {new Date(conn.last_sync_at).toLocaleString()}
                      </span>
                    )}
                    <span>Total synced: {conn.total_leads_synced}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleTest(conn.id)} data-testid={`crm-test-${conn.provider}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Test
                    </Button>
                    <Button size="sm" onClick={() => handleSync(conn.id)} disabled={syncing[conn.id]} data-testid={`crm-sync-${conn.provider}`}>
                      {syncing[conn.id] ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
                      Sync Now
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(conn.id)} data-testid={`crm-delete-${conn.provider}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                {conn.error_message && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-red-500">
                    <AlertTriangle className="h-3 w-3" /> {conn.error_message}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Sync Logs */}
      {syncLogs.length > 0 && (
        <Card data-testid="crm-sync-logs">
          <CardHeader>
            <CardTitle className="text-base">Sync History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {syncLogs.map(log => (
                <div key={log.id} className="flex items-center justify-between text-sm border-b pb-2 last:border-0">
                  <div className="flex items-center gap-2">
                    <Badge variant={log.status === 'completed' ? 'default' : 'destructive'} className="text-xs">
                      {log.status}
                    </Badge>
                    <span className="capitalize">{log.provider}</span>
                    <span className="text-muted-foreground">{log.direction}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Pulled: {log.leads_pulled}</span>
                    <span>Created: {log.leads_created}</span>
                    <span>Pushed: {log.leads_pushed}</span>
                    <span>{new Date(log.started_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {connections.length === 0 && !loading && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Link2 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No CRM connections yet. Add one above to start syncing leads.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
